import { type ChangeEvent, type JSX, useCallback } from 'react';

import type { Hero } from '@schema/index.js';

import type { BuilderStore } from '../state.js';

import { FeaturedTilesEditor } from './FeaturedTilesEditor.js';
import { SelectField, TextField, ToggleField } from './Field.js';
import { RichTextEditor } from './RichTextEditor.js';

export interface HeroEditorProps {
  store: BuilderStore;
}

const HERO_KINDS = [
  { value: 'featured', label: 'Featured tiles — up to 4 large icons' },
  { value: 'clock',    label: 'Clock — large time + optional date' },
  { value: 'weather',  label: 'Weather — clock + current conditions' },
  { value: 'richText', label: 'Rich text — headings, paragraphs, lists' },
  { value: 'none',     label: 'None — hide the hero, grid fills the band' },
] as const;
type HeroKind = typeof HERO_KINDS[number]['value'];

/**
 * Variant picker + per-variant editor for `profile.hero`. The five
 * variants map 1:1 to the renderer's Hero union; switching the picker
 * patches `profile.hero` with a sensible default for that variant.
 *
 * `featured` is the back-compat default — when profile.hero is unset,
 * we read as if it were `{ kind: 'featured' }` so existing configs
 * land here without disturbance.
 */
export function HeroEditor({ store }: HeroEditorProps): JSX.Element {
  const profile = store.activeProfile;
  const hero: Hero = profile.hero ?? { kind: 'featured' };

  /** Switch variant → seed sensible defaults for the new kind. */
  const setKind = useCallback(
    (next: HeroKind): void => {
      const seeded: Hero =
        next === 'featured'
          ? { kind: 'featured' }
          : next === 'clock'
            ? { kind: 'clock', showDate: true }
            : next === 'weather'
              ? { kind: 'weather', showDate: true }
              : next === 'richText'
                ? {
                    kind: 'richText',
                    contentJson: JSON.stringify({
                      type: 'doc',
                      content: [
                        {
                          type: 'paragraph',
                          content: [{ type: 'text', text: 'Edit me in the Builder.' }],
                        },
                      ],
                    }),
                  }
                : { kind: 'none' };
      store.patchProfile({ hero: seeded });
    },
    [store],
  );

  return (
    <div className="editor">
      <header className="editor__head">
        <h2 className="editor__title">Hero band</h2>
        <p className="editor__sub">
          The slab between the launcher's header and tile grid. Pick a
          variant that fits the device's job — featured launches for a
          shift kiosk, large clock for a wall-mounted display, weather
          for a lobby tablet, rich text for an announcement, or{' '}
          <strong>none</strong> to hand the full screen to the tile grid.{' '}
          <a href="https://mobilauncher.com/docs/customize#hero" target="_blank" rel="noopener noreferrer" className="editor__doc-link">
            Read the docs →
          </a>
        </p>
      </header>

      <fieldset className="editor__group" data-testid="hero-variant-fieldset">
        <legend className="editor__group-label">Variant</legend>
        <SelectField
          label="Hero variant"
          hint="The launcher's hero band switches to this variant on next config push."
          value={hero.kind}
          options={HERO_KINDS}
          onChange={(value) => setKind(value)}
        />
      </fieldset>

      {hero.kind === 'featured' && <FeaturedTilesEditor store={store} />}

      {hero.kind === 'clock' && <ClockFields hero={hero} store={store} />}

      {hero.kind === 'weather' && <WeatherFields hero={hero} store={store} />}

      {hero.kind === 'richText' && <RichTextFields hero={hero} store={store} />}

      {hero.kind === 'none' && (
        <fieldset className="editor__group">
          <legend className="editor__group-label">No hero</legend>
          <p className="field__hint">
            The tile grid claims the full launcher band — no header
            content above. Useful for devices with limited screen
            height (rugged handhelds in landscape) where every pixel
            of grid space matters.
          </p>
        </fieldset>
      )}
    </div>
  );
}

function ClockFields({
  hero,
  store,
}: {
  hero: Extract<Hero, { kind: 'clock' }>;
  store: BuilderStore;
}): JSX.Element {
  return (
    <fieldset className="editor__group">
      <legend className="editor__group-label">Clock options</legend>
      <ToggleField
        label="Show date"
        hint="Renders the long-form date (e.g., 'Monday, May 26') under the time."
        value={hero.showDate ?? true}
        onChange={(value) =>
          store.patchProfile({ hero: { ...hero, showDate: value } })
        }
      />
      <TextField
        label="Caption (optional)"
        hint="A short line above the time. Up to 120 characters."
        value={hero.caption ?? ''}
        onChange={(value) =>
          store.patchProfile({
            hero: { ...hero, caption: value === '' ? undefined : value },
          })
        }
      />
    </fieldset>
  );
}

function WeatherFields({
  hero,
  store,
}: {
  hero: Extract<Hero, { kind: 'weather' }>;
  store: BuilderStore;
}): JSX.Element {
  function patch(next: Partial<Extract<Hero, { kind: 'weather' }>>): void {
    store.patchProfile({ hero: { ...hero, ...next } });
  }
  return (
    <fieldset className="editor__group">
      <legend className="editor__group-label">Weather options</legend>
      <ToggleField
        label="Show date"
        hint="Renders the long-form date under the time."
        value={hero.showDate ?? true}
        onChange={(value) => patch({ showDate: value })}
      />
      <NumberField
        label="Latitude (optional)"
        hint="-90 to 90. Leave blank to use the device's geolocation."
        value={hero.latitude}
        onChange={(v) => patch({ latitude: v })}
      />
      <NumberField
        label="Longitude (optional)"
        hint="-180 to 180."
        value={hero.longitude}
        onChange={(v) => patch({ longitude: v })}
      />
      <TextField
        label="Location label (optional)"
        hint="Display name shown under the weather card. Doesn't affect lookup."
        value={hero.locationLabel ?? ''}
        onChange={(value) =>
          patch({ locationLabel: value === '' ? undefined : value })
        }
      />
      <p className="field__hint">
        Weather data: Open-Meteo (free, no API key needed). Updates
        every 10 minutes per device — cached so a fleet of devices
        doesn't hammer the endpoint.
      </p>
    </fieldset>
  );
}

function RichTextFields({
  hero,
  store,
}: {
  hero: Extract<Hero, { kind: 'richText' }>;
  store: BuilderStore;
}): JSX.Element {
  return (
    <fieldset className="editor__group">
      <legend className="editor__group-label">Rich text content</legend>
      <p className="field__hint">
        Bold, italic, headings, bulleted + numbered lists, blockquotes,
        and links are supported. Up to 20 KB.
      </p>
      <RichTextEditor
        contentJson={hero.contentJson}
        onChange={(nextJson) =>
          store.patchProfile({ hero: { ...hero, contentJson: nextJson } })
        }
      />
    </fieldset>
  );
}

/**
 * Number input with empty-string-as-undefined semantics — lets a user
 * clear the latitude/longitude back to "use geolocation" by deleting
 * the field's contents. Refuses non-finite values silently.
 */
function NumberField({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint?: string;
  value: number | undefined;
  onChange: (next: number | undefined) => void;
}): JSX.Element {
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      <input
        className="field__input"
        type="number"
        inputMode="decimal"
        step="any"
        value={value === undefined ? '' : String(value)}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          const raw = e.target.value;
          if (raw === '') {
            onChange(undefined);
            return;
          }
          const parsed = Number(raw);
          if (Number.isFinite(parsed)) onChange(parsed);
        }}
      />
      {hint !== undefined && <span className="field__hint">{hint}</span>}
    </label>
  );
}
